import { clearSessionAndRedirect, REFRESH_TOKEN_KEY, TOKEN_KEY } from './auth-session';
import { shippingApiUrl } from './shipping-api';

interface TokenRefreshResponse {
  access: string;
  refresh?: string;
}

// Any failure in here means the session is dead — every caller (auth link,
// error-link retry) treats a null return the same way, so collapse to a single
// rule: refresh fails → tear down the session and redirect to /login.
// Returning null without redirecting is reserved for the "no session existed
// to begin with" case (e.g. a public page making a query while logged out).
export async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  const hadSession = !!localStorage.getItem(TOKEN_KEY) || !!refreshToken;

  const failSession = (reason: string): null => {
    if (hadSession) clearSessionAndRedirect(reason);
    return null;
  };

  if (!refreshToken) return failSession('Refresh token missing');

  const refreshUrl = shippingApiUrl('/api-token-refresh/');

  try {
    const response = await fetch(refreshUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: refreshToken }),
    });

    if (!response.ok) {
      return failSession(`Token refresh returned ${response.status}`);
    }

    const data: TokenRefreshResponse = await response.json();
    if (!data.access) return failSession('Token refresh response missing access token');

    localStorage.setItem(TOKEN_KEY, data.access);
    if (data.refresh) localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh);
    return data.access;
  } catch (error) {
    console.error('[Token Refresh] Error refreshing token:', error);
    return failSession('Token refresh request threw');
  }
}

export function isTokenExpiredOrExpiringSoon(token: string | null, bufferSeconds: number = 60): boolean {
  if (!token) return true;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1]));
    const exp = payload.exp;
    if (!exp) return false;
    const now = Math.floor(Date.now() / 1000);
    return exp <= now + bufferSeconds;
  } catch {
    return true;
  }
}

export async function refreshTokenIfNeeded(): Promise<string | null> {
  const currentToken = localStorage.getItem(TOKEN_KEY);
  if (isTokenExpiredOrExpiringSoon(currentToken)) {
    return refreshAccessToken();
  }
  return currentToken;
}
